import * as yup from 'yup';
import clsx from 'clsx';
import { Box, Grid, InputAdornment, makeStyles, Table, TableBody, TableCell, TableRow, Typography  } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { InvestmentOptions } from '../../../api';
import RadioList from './RadioList';
import { investmentOptionOptions } from '../../../common/types';
import { Colors, ErrorMessage, HelpTooltip } from '../helpers';
import NumberTextInput from './NumberTextInput';

interface InvestmentOptionProps {
    kiwiSaver?: boolean;
    showBalancedOption?: boolean;
    namePrefix?: string;
    validationKey?: string;
    value?: {
        investmentOption?: InvestmentOptions;
        growth?: number;
        inflation?: number;
        income?: number;
    };
    horizontal?: boolean;
}

export const FUNDS = [
    {
        key: 'income',
        name: 'Income Strategy',
        description: 'Objective: To provide exposure to income assets, anticipated to mainly hold New Zealand, Australian and international bonds.'
    },
    {
        key: 'inflation',
        name: 'Inflation Strategy',
        description: 'Objective: To mitigate the impact of inflation on your investment over the medium/long term, anticipated to mainly hold New Zealand, Australian and international bonds and shares.'
    },
    {
        key: 'growth',
        name: 'Growth Strategy',
        description: 'Objective: To grow your investment over the long term, anticipated to mainly hold New Zealand, Australian and international shares, and/or hedge funds.'
    }
]

export const investmentOptionValidationSchema = yup.object({
    investmentOption: yup.mixed<InvestmentOptions>().required('An investment option is required')
}).test('100% allocated', 'Total allocation must be 100%', function (values: any) {
    if (values.investmentOption !== InvestmentOptions.SelfSelect) return true;
    return totalPercent(values) === 100 || this?.createError({
        path: 'totalAllocated',
        message: 'Total allocation must be 100%'
    });
});

const totalPercent = (values: any) => FUNDS.reduce((sum, fund) => sum + (Number(values[fund.key]) || 0), 0)

const InvestmentOption = ({ horizontal, kiwiSaver, namePrefix, showBalancedOption, validationKey, value }: InvestmentOptionProps) => {
    const classes = useStyles();
    const _namePrefix = namePrefix ? `${namePrefix}.` : '';
    const investmentOptions = investmentOptionOptions({ kiwiSaver, showBalancedOption });

    return <>
        <Grid item xs={12}>
            <RadioList name={_namePrefix + "investmentOption"} horizontal={horizontal} options={investmentOptions} value={value?.investmentOption} />
        </Grid>
        <Grid item xs={12}>
            {value?.investmentOption === InvestmentOptions.SelfSelect &&
                <Table>
                    <TableBody>
                        {FUNDS.map(({ key, name, description }) =>
                            <TableRow key={key}>
                                <TableCell className={classes.tableCell}>
                                    <Typography className={classes.textWithHelpIcon}>
                                        {name}
                                        <HelpTooltip title={description} />
                                    </Typography>
                                </TableCell>
                                <TableCell className={clsx(classes.tableCell, classes.percentCell)}>
                                    <NumberTextInput
                                        name={_namePrefix + key} 
                                        mask="999"
                                        placeholder="0"
                                        className={classes.percentInput}
                                        endAdornment={<InputAdornment position="end" disablePointerEvents>%</InputAdornment>} />
                                </TableCell>
                            </TableRow>
                        )}
                        <TableRow>
                            <TableCell className={clsx(classes.tableCell, classes.totalCell)}>
                                <Typography><b>Total</b></Typography>
                            </TableCell>
                            <TableCell className={clsx(classes.tableCell, classes.percentCell, classes.totalCell)}>
                                <Box display="flex" justifyContent="flex-end">
                                    <Typography><b>{totalPercent(value)}&nbsp;%</b></Typography>
                                    {totalPercent(value) === 100 ? <CheckCircleIcon className={classes.tick} /> : <CancelIcon className={classes.cross} />}
                                </Box>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            }
        </Grid>
        <ErrorMessage name={validationKey || 'totalAllocated'} />
    </>
}

const useStyles = makeStyles({
    tableCell: {
        padding: '5px 0 5px 0',
        border: 'unset'
    },
    percentCell: {
        width: 95,
        textAlign: 'right'
    },
    totalCell: {
        padding: '10px 0 15px 0',
        borderTop: `1px solid ${Colors.Grey} !important`
    },
    percentInput: {
        '& .MuiTypography-root': {
            paddingRight: 10,
            color: Colors.Black
        },
        '& input': {
            textAlign: 'right'
        },
        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
            '-webkit-appearance': 'none'
        },
        '& input[type=number]': {
            '-moz-appearance': 'textfield'
        },
        '& .MuiInputAdornment-root .MuiTypography-root': {
            position: 'relative',
            left: -5,
            pointerEvents: 'none'
        }
    },
    tick: {
        color: Colors.Green,
        marginLeft: 5
    },
    cross: {
        color: Colors.Red,
        marginLeft: 5
    },
    textWithHelpIcon: {
        display: 'flex',
        alignItems: 'center'
    }
}, { name: 'InvestmentOption' });

export default InvestmentOption;
